import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Mail, X, Check } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getMyAccessRequests, respondToAccessRequest } from "../../services/accessRequestService";
import Loader from "../../components/common/Loader";
import Card from "../../components/common/Card";
import Badge from "../../components/common/Badge";
import EmptyState from "../../components/common/EmptyState";
import ConfirmModal from "../../components/common/ConfirmModal";
import { color } from "../../styles/theme";

const STATUS_VARIANT = { 0: "warning", 1: "success", 2: "danger", 3: "danger" };

const formatDate = (d, t) => (d ? new Date(d).toLocaleString("en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : t("unknownDate"));

const isPrivateItem = (item) => item?.Origin === "Private" || item?.EventOrigin === "Private" || item?.Event?.Origin === "Private";

/**
 * Access Requests — ported from mobile's pending-event-access screen
 * (src/Screens/pending-event-access). The receiving side of Phase 4's
 * host-request feature: incoming invites from other event owners asking
 * this promoter to be a host/helper on their events.
 */
const AccessRequests = () => {
  const { t } = useTranslation(["accessRequests", "common"]);
  const STATUS_LABEL = { 0: t("status.pending"), 1: t("status.approved"), 2: t("status.rejected"), 3: t("status.cancelled") };
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const load = () => {
    setLoading(true);
    getMyAccessRequests()
      .then((res) => setRequests(res.data?.Result || []))
      .catch(() => toast.error(t("toast.loadFailed")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const applyStatus = (id, status) => {
    setRequests((prev) => prev.map((r) => (r.Id === id ? { ...r, Status: status } : r)));
  };

  const handleAccept = async (item) => {
    if (user?.PhoneNumberConfirmed !== true) {
      toast.info(t("toast.verifyPhone"));
      navigate("/settings");
      return;
    }
    setActingId(item.Id);
    try {
      const res = await respondToAccessRequest(item.Id, true);
      toast.success(res.data?.Message || t("toast.approveSuccess"));
      applyStatus(item.Id, 1);
      if (item.ChatRoomId) navigate(`/events/${item.ChatRoomId}`);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("toast.updateFailed"));
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActingId(rejectTarget.Id);
    try {
      const res = await respondToAccessRequest(rejectTarget.Id, false);
      toast.success(res.data?.Message || t("toast.rejectSuccess"));
      applyStatus(rejectTarget.Id, 2);
      setRejectTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("toast.updateFailed"));
    } finally {
      setActingId(null);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <h2 style={s.title}>{t("title")}</h2>

      {requests.length === 0 ? (
        <EmptyState icon={Mail} text={t("empty")} />
      ) : (
        <div style={s.list}>
          {requests.map((item) => {
            const status = Number(item.Status);
            const isPending = status === 0;
            const hostNoun = isPrivateItem(item) ? t("helper") : t("host");
            return (
              <Card key={item.Id} style={s.card}>
                <div style={s.headerRow}>
                  <div style={s.eventRow}>
                    {item.EventThumbnailUrl && <img src={item.EventThumbnailUrl} alt="" style={s.thumb} />}
                    <span style={s.eventName}>{item.EventName || t("event")}</span>
                  </div>
                  <Badge variant={STATUS_VARIANT[status] || "warning"}>{STATUS_LABEL[status] || t("status.pending")}</Badge>
                </div>

                <div style={s.metaRow}><span style={s.metaLabel}>{t("requestedBy")}</span> {item.RequestedByName || t("unknown")}</div>
                <div style={s.metaRow}><span style={s.metaLabel}>{t("requestedOn")}</span> {formatDate(item.CreatedAt, t)}</div>

                {isPending && (
                  <div style={s.actions}>
                    <button type="button" className="btn btn-danger-ghost" style={{ ...s.actionBtn, border: "1.5px solid #fecaca" }} onClick={() => setRejectTarget(item)} disabled={actingId === item.Id}>
                      <X size={14} strokeWidth={2.25} /> {t("rejectRequest", { hostNoun })}
                    </button>
                    <button type="button" className="btn btn-primary" style={{ ...s.actionBtn, background: color.success500 }} onClick={() => handleAccept(item)} disabled={actingId === item.Id}>
                      {actingId === item.Id ? t("common:actions.pleaseWait") : <><Check size={14} strokeWidth={2.5} /> {t("approveRequest", { hostNoun })}</>}
                    </button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {rejectTarget && (
        <ConfirmModal
          title={t("rejectModal.title")}
          message={t("rejectModal.message", {
            hostNoun: isPrivateItem(rejectTarget) ? t("helperLower") : t("hostLower"),
            requester: rejectTarget.RequestedByName || t("thisPromoter"),
            eventName: rejectTarget.EventName || t("thisEvent"),
          })}
          confirmText={t("reject")}
          danger
          loading={actingId === rejectTarget.Id}
          onConfirm={handleReject}
          onCancel={() => actingId !== rejectTarget.Id && setRejectTarget(null)}
        />
      )}
    </div>
  );
};

const s = {
  title: { fontSize: 20, fontWeight: 800, color: color.textPrimary, marginBottom: 20, letterSpacing: -0.3 },
  list: { display: "flex", flexDirection: "column", gap: 14, maxWidth: 560 },
  card: { padding: 18 },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10 },
  eventRow: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  thumb: { width: 30, height: 30, borderRadius: 15, objectFit: "cover", flexShrink: 0 },
  eventName: { fontWeight: 700, fontSize: 14, color: color.primary600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  metaRow: { fontSize: 12.5, color: color.gray700, marginBottom: 6 },
  metaLabel: { color: color.textMuted, fontWeight: 600 },
  actions: { display: "flex", gap: 10, marginTop: 12 },
  actionBtn: { flex: 1, justifyContent: "center" },
};

export default AccessRequests;
